import lottie from "lottie-web"
import Bugsnag from "@bugsnag/js"

import { load, store, getStoreByKeyValue } from "./localStorage"
import renewedThankYou from "./../animations/congrats-renewed.json"
import { logEvent, logEvents } from "./logger"

const KGS_IN_LBS = 0.453592
const LBS_IN_KG = 2.20462
const INCH_IN_CM = 2.54
const CM_IN_FT = 30.48
const LOCAL_STORAGE_KEY = "data"
const INVALID_DOMAIN_CODE = "invalid_domain"
const EMAIL_VALIDATION_ERROR = "email: Invalid email"
const EMAIL_VALIDATION_CODE = "email_verification_failed"

const round = Math.round
export const kgToLbs = (kg) => kg / KGS_IN_LBS
export const lbsToKg = (lbs) => lbs / LBS_IN_KG
export const cmToFtIn = (cm) => {
  const totalInch = cm / INCH_IN_CM
  const feet = Math.floor(totalInch / 12)
  const inch = totalInch - 12 * feet

  return { heightFt: Math.round(feet), heightIn: Math.round(inch) }
}

export const ftInToCm = ({ ft, inch }) => {
  const heightCm = Math.round(ft * CM_IN_FT + inch * INCH_IN_CM)

  return heightCm
}

function initialState() {
  return {
    // Globals
    token: null,
    measurementSystem: pipelineConfig.measurementSystem,
    domain: null,
    goal: "Lose weight",
    // Cookies
    cookieAgreed: null,
    requiredCookies: true,
    analyticalCookies: true,
    marketingCookies: true,
    cookiePolicyBannerAccepted: false,

    // Step 0
    landingProceeded: null,
    mainGoal: null,

    // Step 01-hunger
    hunger: null,
    // Step 02-energy
    energy: null,
    // Step 03-weight
    weightKg: null,
    weightLbs: null,
    // Step 04-age
    age: null,
    // Step 05-height
    heightCm: null,
    heightFt: null,
    heightIn: null,
    // Step 06-gender
    gender: null,
    // Step 07-name
    firstName: null,

    // Step 10-ideal weight
    targetWeightKg: null,
    targetWeightLbs: null,

    // Step 13-plan-apetite
    apetite: null,
    ratherEat: null,
    smoking: null,

    // Step 14-plan-like
    likeBetter: null,
    beverageLikeBetter: null,
    feelBloated: null,

    // Step 15-plan-first-meal
    firstMealAfter: null,
    hormonalContraceptions: null,
    bonePain: null,
    fallingAsleep: null,

    // Step 16-plan-frequency
    eatingMeatOften: null,
    likeBetter2: null,
    hiccups: null,
    childrenUpTo18: null,
    alcoholConsumption: null,

    // Step 17-create-plan
    marketingAgreed: null,
    milkAllergy: null,
    privacyAgreement: null,
    weightLossReceive: null,
    email: null,

    //Step 19-checkout
    paymentPrice: null,
    showDiscountModal: false,

    isAbandonedBasketOrigin: false,
    exitIntent: false,
    paymentMethod: null,
    paymentSuccessUrl: "",
    paymentFailUrl: "",
    paymentUrl: null, // comes from backend when paymentMethod is set
    errorResponseData: null,
    segmentUserId: null,
    primer_pipeline_metadata: pipelineConfig.primerMetadata,
  }
}

export default {
  // Push data store to backend API
  init() {
    const data = load(LOCAL_STORAGE_KEY)

    if (data && data.values) {
      this.values = data.values
    } else {
      this.values = initialState()
    }

    this.storeAnalytics()

    this.paymodesUrls = {
      primer: {
        successUrl: `${window.location.origin}${pipelineConfig.pathPrefix}${pipelineConfig.pagePaths["22-thank-you"]}`,
        failUrl: `${window.location.origin}${pipelineConfig.pathPrefix}${pipelineConfig.pagePaths["21-registration-failure"]}?error=primer`,
      },
      paypal_recurring: {
        successUrl: `${window.location.origin}${pipelineConfig.pathPrefix}${pipelineConfig.pagePaths["22-thank-you"]}`,
        failUrl: `${window.location.origin}${pipelineConfig.pathPrefix}${pipelineConfig.pagePaths["21-registration-failure"]}?error=paypal_recurring`,
      },
    }

    this.abPaymodesUrls = {
      primer: {
        successUrl: `${window.location.origin}${pipelineConfig.pathPrefix}${pipelineConfig.pagePaths["24-ab-thank-you"]}`,
        failUrl: `${window.location.origin}${pipelineConfig.pathPrefix}${pipelineConfig.pagePaths["23-ab-registration-failure"]}?error=primer`,
      },
      paypal_recurring: {
        successUrl: `${window.location.origin}${pipelineConfig.pathPrefix}${pipelineConfig.pagePaths["24-ab-thank-you"]}`,
        failUrl: `${window.location.origin}${pipelineConfig.pathPrefix}${pipelineConfig.pagePaths["23-ab-registration-failure"]}?error=paypal_recurring`,
      },
    }

    Alpine.effect(() => {
      store(LOCAL_STORAGE_KEY, { values: this.values })
    })
  },

  storeAnalytics() {
    const analyticsData = {}

    analyticsData.domain = document.location.host

    // Add cookies - _ga, _gid, _fbc, _fbp, ...
    document.cookie.split("; ").forEach((item) => {
      const [key, value] = item.split("=")
      analyticsData[key] = value
    })

    // Add search params - fbclid, utm_*, ...
    const url = new URL(document.location)
    url.searchParams.forEach((value, key) => {
      analyticsData[key] = value
    })

    // Add FraudNet data
    analyticsData["fraud_net_session_id"] = localStorage.getItem("fraud_net_session_id") || ""

    this.values = { ...this.values, ...analyticsData }
  },

  writeToLocalStorage(data) {
    window.localStorage.setItem(
      `${pipelineConfig.pathPrefix}:${LOCAL_STORAGE_KEY}`,
      JSON.stringify({ values: data })
    )
  },

  getFromLocalStorage(key) {
    const val = getStoreByKeyValue(LOCAL_STORAGE_KEY, key)
    return typeof val === "string" ? val : null
  },

  // Support boolean values
  convertValuesToBooleans(values) {
    const converted = {}

    Object.keys(values).forEach((key) => {
      let convertedValue

      switch (values[key]) {
        case "true":
          convertedValue = true
          break

        case "false":
          convertedValue = false
          break

        default:
          convertedValue = values[key]
      }

      converted[key] = convertedValue
    })

    return converted
  },

  convertValuesFromBooleans(values) {
    const dataValuesToConvert = ["marketingAgreed"]
    const converted = {}

    Object.keys(values).forEach((key) => {
      let convertedValue

      if (dataValuesToConvert.includes(key)) {
        switch (values[key]) {
          case true:
            convertedValue = "true"
            break

          case false:
            convertedValue = "false"
            break

          default:
            convertedValue = values[key]
        }
      }

      converted[key] = convertedValue || values[key]
    })

    return converted
  },

  async pull() {
    if (this.token) {
      const res = await fetch(pipelineConfig.orderApiUrl, {
        method: "GET",
        headers: {
          "content-type": "application/json",
          "x-token": this.token,
        },
      })
      const data = await res.json()
      const convertedData = this.convertValuesFromBooleans(data)

      Object.entries(convertedData).forEach(([key, value]) => {
        this[key] = value
      })

      await this.identifySegmentUser(this.segmentUserId, this.email)

      this.writeToLocalStorage({
        ...data,
        showDiscountModal: false,
        exitIntent: true,
        isAbandonedBasketOrigin: true,
      })
    }
  },

  // Push data store to backend API
  async push() {
    this.values.errorResponseData = null // Init error to null;
    const apiUrl = pipelineConfig.orderApiUrl

    const convertedValues = this.convertValuesToBooleans(this.values)

    const options = {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(convertedValues),
    }

    if (this.values.token) {
      options.method = "PATCH"
      options.headers["x-token"] = this.values.token
    }

    try {
      const res = await fetch(apiUrl, options)
      const data = await res.json()

      if (res.ok) {
        this.error = undefined
        this.values.errorResponseData = null

        const convertedData = this.convertValuesFromBooleans(data)

        Object.entries(convertedData).forEach(([key, value]) => {
          this.values[key] = value
        })

        await this.identifySegmentUser(this.values.segmentUserId, this.values.email)
      } else {
        window.analytics.track(window.getEventName("Invalid Form Submitted"), {
          values: this.values,
          error: data.error,
        })

        if (
          data.code === INVALID_DOMAIN_CODE ||
          data.error === EMAIL_VALIDATION_ERROR ||
          data.code === EMAIL_VALIDATION_CODE
        ) {
          this.values.errorResponseData = data
          this.error = data.error
        } else {
          this.error = data.error
        }
      }
    } catch (error) {
      window.analytics.track(window.getEventName("Error FailedToFetch Displayed"))
      this.error = error
      Bugsnag.notify(error)
    }

    return !this.error
  },

  redirect(page) {
    if (!page) return

    window.location.pathname = page
  },

  agreeToCookies() {
    this.values.cookieAgreed = true
  },

  toggleAnalyticalCookies() {
    this.values.analyticalCookies = !this.values.analyticalCookies
  },

  toggleMarketingCookies() {
    this.values.marketingCookies = !this.values.marketingCookies
  },

  landingProceed() {
    this.values.landingProceeded = true
  },

  setMainGoal(goal, href) {
    this.values.mainGoal = goal

    this.redirect(href)
  },

  setMeasurementSystem(value) {
    if (!value) return

    window.analytics.track(window.getEventName("Units Clicked"), {
      value,
    })

    this.values.measurementSystem = value
  },

  setGender(value) {
    this.values.gender = value
  },

  getProgressWidth(activeStepNumber) {
    if (activeStepNumber < 12) {
      return (activeStepNumber * 2.7).toFixed(2)
    }

    if (activeStepNumber < 19) {
      return (activeStepNumber * 3.53).toFixed(2)
    }

    if (activeStepNumber > 18 && activeStepNumber < 24) {
      return "85"
    }

    if (activeStepNumber == 22 || activeStepNumber == 24) {
      return "100"
    }
  },

  get normalizedHeight() {
    if (this.isMetric) {
      return this.values.heightCm
    } else {
      return this.values.heightFt * 30.48 + this.values.heightIn * 2.54
    }
  },

  get normalizedWeight() {
    if (this.isMetric) {
      return this.values.weightKg
    } else {
      return this.values.weightLbs * KGS_IN_LBS
    }
  },

  get isFemale() {
    return this.values.gender === "female"
  },

  get isMale() {
    return this.values.gender === "male"
  },

  get weightUnits() {
    return this.isMetric ? "kg" : "lbs"
  },

  get heightUnits() {
    return this.isMetric ? "cm" : "ft"
  },

  get isMetric() {
    return this.values.measurementSystem === "metric"
  },

  get isImperial() {
    return this.values.measurementSystem === "imperial"
  },

  get loseWeight() {
    return this.values.goal === "Lose weight"
  },

  get bmi() {
    return (this.normalizedWeight / (this.normalizedHeight / 100.0) ** 2).toFixed(0)
  },

  get overWeight() {
    return this.normalizedWeight - this.idealWeight
  },

  get idealWeight() {
    let value = round((this.normalizedHeight ** 2 * 21.1) / 10000)
    if (value < 30) {
      value = 30
    }
    return value
  },

  get idealWeightRange() {
    const minBMI = 18
    const maxBMI = 25
    const lowestWeightValue = this.isImperial ? round(kgToLbs(30)) : 30
    const normalizedHeight = this.normalizedHeight / 100.0

    let min = minBMI * Math.pow(normalizedHeight, 2)
    let max = maxBMI * Math.pow(normalizedHeight, 2)

    if (min >= max) {
      min = max - 1
    }

    if (this.isImperial) {
      min = kgToLbs(min)
      max = kgToLbs(max)
    }

    min = Math.max(round(min), lowestWeightValue)
    max = Math.max(round(max), lowestWeightValue)

    const idealRangeArray = []

    for (let i = min; i <= max; i++) {
      idealRangeArray.push(i)
    }

    return idealRangeArray
  },

  get idealWeightRangeOptions() {
    const idealRange = this.idealWeightRange
    const options = idealRange.map((val) => ({
      title: `${val}`,
      value: `${val}`,
    }))

    return options
  },

  get enabledDiscount() {
    return this.values.exitIntent
  },

  get showDiscountModal() {
    if (this.values.showDiscountModal) {
      logEvent(logEvents.lastChancePopupViewed, {
        orderId: this.values?.id,
        userId: this.values?.userId,
      })
    }

    return this.values.showDiscountModal
  },

  formattedHeight() {
    const { heightCm, heightFt, heightIn } = this.values

    if (this.isImperial) {
      return pipelineConfig.dir === "rtl"
        ? `in${heightIn} ft${heightFt}`
        : `${heightFt}ft ${heightIn}in`
    } else {
      return pipelineConfig.dir === "rtl" ? `cm${heightCm}` : `${heightCm}cm`
    }
  },

  formatWeight(value) {
    return pipelineConfig.dir === "rtl"
      ? `${this.weightUnits}${value}`
      : `${value}${this.weightUnits}`
  },

  formattedWeight() {
    return this.formatWeight(this.isMetric ? this.values.weightKg : this.values.weightLbs)
  },

  formattedIdealWeight() {
    return this.formatWeight(this.isMetric ? this.idealWeight : round(kgToLbs(this.idealWeight)))
  },

  formattedMinIdealWeight() {
    return this.formatWeight(this.idealWeightRange[0])
  },

  formattedMaxIdealWeight() {
    return this.formatWeight(this.idealWeightRange.slice(-1)[0])
  },

  formattedWeightToLose() {
    const { weightKg, targetWeightKg, weightLbs, targetWeightLbs } = this.values

    return this.formatWeight(
      this.isMetric ? weightKg - targetWeightKg : weightLbs - targetWeightLbs
    )
  },

  formattedWeightGoal() {
    return this.formatWeight(
      this.isMetric ? this.values.targetWeightKg : this.values.targetWeightLbs
    )
  },

  isAbandonedBasketPage() {
    const abPath = `${pipelineConfig.pathPrefix}${pipelineConfig.pagePaths["20-abandoned-basket"]}`
    const isAbandonedBasketPage = window.location.pathname.includes(abPath)

    return isAbandonedBasketPage
  },

  async enableDiscount() {
    this.values.exitIntent = true
    this.values.paymentPrice = pipelineConfig.exitIntentDisplayPrice
    this.setShowDiscountModal(false)

    // Re-init checkout to apply last chance price configuration
    window.location.reload()
  },

  // With current implemetation, the modal is rendered immediately
  // and lets user to click the accept button before Alpine is fully inited.
  // This leads to the bug when modal can be rendered multiple times.
  enableDiscountFromModal() {
    if (document.readyState !== "complete") {
      document.addEventListener("readystatechange", () => {
        if (document.readyState === "complete") {
          this.enableDiscount()
        }
      })
    } else {
      this.enableDiscount()
    }
  },

  formattedPaymentMethod() {
    const paymentMethod = this.values.paymentMethod

    return paymentMethod
  },

  setShowDiscountModal(value) {
    this.values.showDiscountModal = value
  },

  handleInternalRedirect(activeStepNumber, exitIntentHandlerPath) {
    if (activeStepNumber == 19 && !this.enabledDiscount) {
      this.redirect(exitIntentHandlerPath)
    } else {
      history.back()
    }
  },

  handleCheckoutRedirect(nextStepPath) {
    this.setShowDiscountModal(!this.enabledDiscount)

    this.redirect(nextStepPath)
  },

  async trackVisitCheckout() {
    this.values.status = "VISIT_CHECKOUT"

    // For escaping: "feature/translations" -> "feature/translations-inline-checkout-improvements" error for AB switching AB url
    // Meaning if users hasn't pressed pay() on "feauture/translations". Order success | fail urls are empty and cant patch Order.
    const successUrl = this.values.paymentSuccessUrl
    const failUrl = this.values.paymentFailUrl
    const isABPath = this.isAbandonedBasketPage()
    if (!successUrl || !failUrl) {
      if (isABPath) {
        const { successUrl, failUrl } = this.getPaymodesUrls("primer")

        this.values.paymentSuccessUrl = successUrl
        this.values.paymentFailUrl = failUrl
      }
    }

    await this.push()
  },

  goToLogin() {
    const loginUrl = this.values.loginUrl
    setTimeout(function () {
      this.values = initialState()
      window.location = loginUrl || pipelineConfig.loginUrl
    }, 1000)
  },

  resetLocalStorage() {
    const initialValues = initialState()
    const valuesToOmit = ["cookiePolicyBannerAccepted"]
    const omittedValues = {}
    Object.keys(this.values).forEach((key) => {
      if (valuesToOmit.includes(key)) {
        omittedValues[key] = this.values[key]
      }
    })

    this.values = {
      ...initialValues,
      ...omittedValues,
    }
    this.storeAnalytics()
  },

  loadAnimation() {
    lottie.loadAnimation({
      container: document.querySelector("#thankyou-animation"),
      animationData: renewedThankYou,
      renderer: "svg",
      loop: true,
      autoplay: true,
    })
  },

  //
  // Payments
  //
  async initiateCheckout(invoiceIdentifier, checkout) {
    const { successUrl, failUrl } = this.getPaymodesUrls("primer")

    this.values.invoiceIdentifier = invoiceIdentifier
    this.values.initiateCheckoutAt = new Date()
    this.values.paymentSuccessUrl = successUrl
    this.values.paymentFailUrl = failUrl
    this.values.checkout = checkout
    this.values.visitCheckoutAt = new Date()
    this.values.preloaded = false

    await this.push()
  },

  async completePayment() {
    this.values.completedAt = new Date()

    await this.push()
  },

  getPipelineID() {
    const url = pipelineConfig.orderApiUrl
    const pipelinesMatchedString = url.match(/pipelines\/+\d+\//) // atrod 'pipelines/71/'

    return pipelinesMatchedString[0].split("/")[1] // atrod 'pipelines , 71' un tu paņem 71
  },

  getGA4SessionId() {
    try {
      const cookies = document.cookie
        .split("; ")
        .find((item) => item.includes("_ga_"))
        .split("=")[1]
      return cookies.split(".")[2]
    } catch (e) {
      console.error(`GA4 session ID not found`, e)
    }
    return undefined
  },

  async markPaymentAsFailed() {
    this.values.failedPayment = true

    await this.push()
  },

  getPrimerMetadata() {
    try {
      return pipelineConfig.primerMetadata
    } catch (error) {}

    return undefined
  },

  async createPayment(SUCCESS_URL, FAIL_URL, offerId, userId, paymodeId) {
    const res = await fetch(
      `${pipelineConfig.productApiUrl}/pay/checkout/${offerId}/${userId}/${paymodeId}`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({
          chance: this.values.exitIntent,
          origin_id: this.values.id,
          origin_host: `https://${this.values.domain}`,
          origin_path: pipelineConfig.pathPrefix,
          successful_payment_path: SUCCESS_URL,
          failed_payment_path: FAIL_URL,
          utm_source: this.values.utm_source || "",
          utm_medium: this.values.utm_medium || "",
          utm_campaign: this.values.utm_campaign || "",
          utm_term: this.values.utm_term || "",
          utm_content: this.values.utm_content || "",
          initiated_remotely: true,
          external_sync: true,
          token: this.values.token,
          ga_cid: this.values._ga?.match(/[^\.]+\.[^\.]+$/)?.[0],
          pipelineId: this.getPipelineID(),
          ga_session_id: this.getGA4SessionId(),
          fbc: this.values._fbc,
          fbp: this.values._fbp,
          fb_ads_id: this.values.fb_ads_id || undefined,
          fb_adset_id: this.values.fb_adset_id || undefined,
          fraud_net_session_id: this.values.fraud_net_session_id,
          primer_pipeline_metadata: {
            ...this.getPrimerMetadata(),
          },
        }),
      }
    )

    const data = await res.json()

    this.values.checkout = data
    this.values.invoiceIdentifier = data.invoice.identifier
    this.values.initiateCheckoutAt = new Date().toISOString()
    this.values.paymentPrice = this.enabledDiscount
      ? pipelineConfig.exitIntentDisplayPrice
      : pipelineConfig.standardDisplayPrice

    return data
  },

  async createPrimerSession(productOfferId) {
    const PRIMER_PAYMODE_ID = 15
    const { successUrl, failUrl } = this.getPaymodesUrls("primer")
    const USER_ID = this.values.userId

    const data = await this.createPayment(
      successUrl,
      failUrl,
      productOfferId,
      USER_ID,
      PRIMER_PAYMODE_ID
    )

    logEvent(logEvents.primerSessionCreated, {
      orderId: this.values?.id,
      userId: this.values?.userId,
      invoiceId: data?.invoice?.id,
    })

    this.values.preloaded = true

    this.writeToLocalStorage()

    return data
  },

  async createRedirectPayment(paymode) {
    const paymodesIds = {
      paypal_recurring: 3,
      stripe: 13,
      primer: 15,
    }

    const { successUrl, failUrl } = this.getPaymodesUrls(paymode)
    const USER_ID = this.values.userId

    const data = await this.createPayment(
      successUrl,
      failUrl,
      pipelineConfig.productOfferId,
      USER_ID,
      paymodesIds[paymode]
    )

    logEvent(logEvents.redirectPayment, {
      orderId: this.values?.id,
      userId: this.values?.userId,
      invoiceId: data?.invoice?.id,
    })

    this.writeToLocalStorage()

    window.location = data.url
  },

  getPaymodesUrls(paymode) {
    const isABPath = this.isAbandonedBasketPage()

    if (isABPath) {
      return this.abPaymodesUrls[paymode]
    }

    return this.paymodesUrls[paymode]
  },

  isErrorBannerAllowed() {
    // Do not display for email step
    if (window.location.pathname.includes(pipelineConfig.pagePaths["17-create-plan"])) {
      return false
    }

    return true
  },

  async identifySegmentUser(segmentUserId, email) {
    if (!segmentUserId) {
      return
    }
    if (window.localStorage.getItem("ajs_user_id") !== segmentUserId) {
      window.localStorage.setItem("ajs_user_id", segmentUserId)
      await window.analytics.identify(segmentUserId, { email })
    }
  },
}
